import type { ReactNode } from "react";
import { fillTemplate, telegramDisplayText } from "./format";

// A template body as the customer will see it (spec 17 §4.1 and §4.3, D-329).
// A Telegram body is the §2.5 HTML subset, and the template picker and the
// variant editor printed it raw, so a manager read `<b>Order {{order_no}}</b>`
// where the customer reads a bold order number (task #281). Here the subset is
// drawn as the formatting it means: <b> bold, <i> italic, <code> monospace and
// <a href> a link. Values are placed escaped, as the server's
// renderTemplateBody places them, so a value with `<` in it reads as typed. A
// body that does not parse as the subset (a draft half typed in the editor)
// is shown as plain text with its tags removed, never as markup. A WhatsApp
// body is plain text and is shown as it is. Pure.

const TAG =
  /<(b|i|code)[ \t\n\r]*>|<a[ \t\n\r]+href="([^"]*)"[ \t\n\r]*>|<\/(b|i|code|a)[ \t\n\r]*>/gu;

type Open = "b" | "i" | "code" | "a";

interface Frame {
  tag: Open | null;
  href: string | null;
  children: ReactNode[];
}

function escapeTelegram(value: string): string {
  return value.replace(/&/gu, "&amp;").replace(/</gu, "&lt;").replace(/>/gu, "&gt;");
}

function element(frame: Frame, key: number): ReactNode {
  switch (frame.tag) {
    case "b":
      return <strong key={key}>{frame.children}</strong>;
    case "i":
      return <em key={key}>{frame.children}</em>;
    case "code":
      return <code key={key}>{frame.children}</code>;
    case "a": {
      const href = frame.href ?? "";
      // Only a web link is followed from a preview; anything else reads as
      // the link text it is.
      return /^https?:\/\//iu.test(href) ? (
        <a key={key} href={href} target="_blank" rel="noopener noreferrer">
          {frame.children}
        </a>
      ) : (
        <span key={key} className="msg-preview-link">
          {frame.children}
        </span>
      );
    }
    default:
      return frame.children;
  }
}

/** The §2.5 subset drawn as React nodes, or null when the body is not well
 * formed (an unknown close, a mismatched close, or a tag left open). */
export function telegramNodes(body: string): ReactNode[] | null {
  const stack: Frame[] = [{ tag: null, href: null, children: [] }];
  let key = 0;
  let at = 0;
  const top = (): Frame => {
    const frame = stack.at(-1);
    if (frame === undefined) throw new Error("unreachable: the root frame is never popped");
    return frame;
  };
  for (const match of body.matchAll(TAG)) {
    const index = match.index;
    if (index > at) top().children.push(telegramDisplayText(body.slice(at, index)));
    at = index + match[0].length;
    const [, openTag, href, closeTag] = match;
    if (openTag !== undefined) {
      stack.push({ tag: openTag as Open, href: null, children: [] });
    } else if (href !== undefined) {
      stack.push({ tag: "a", href: telegramDisplayText(href), children: [] });
    } else {
      const frame = top();
      if (stack.length === 1 || frame.tag !== closeTag) return null;
      stack.pop();
      key += 1;
      top().children.push(element(frame, key));
    }
  }
  if (stack.length !== 1) return null;
  if (at < body.length) top().children.push(telegramDisplayText(body.slice(at)));
  return top().children;
}

/** A template body with its values placed, as the customer reads it. */
export function TemplatePreview({
  body,
  provider,
  variables,
  values,
}: {
  body: string;
  provider: "telegram" | "whatsapp";
  variables: readonly { key: string; sample: string }[];
  values: Readonly<Record<string, string>>;
}) {
  if (provider === "whatsapp") return <>{fillTemplate(body, variables, values)}</>;
  const escaped: Record<string, string> = {};
  for (const [k, v] of Object.entries(values)) escaped[k] = escapeTelegram(v);
  const samples = variables.map((v) => ({ key: v.key, sample: escapeTelegram(v.sample) }));
  const filled = fillTemplate(body, samples, escaped);
  const nodes = telegramNodes(filled);
  return <>{nodes ?? telegramDisplayText(filled)}</>;
}
